import { useMemo, useState } from "react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";

import { appCardForToolkit, COMPOSER_FEATURED_TOOLKITS } from "./morrowLandingCatalog";

const EMPTY_TOOLKIT_SET: ReadonlySet<string> = new Set();

interface ComposerUnconnectedToolsStripProps {
  readonly connectedToolkits: ReadonlySet<string>;
  readonly connectingToolkits?: ReadonlySet<string>;
  readonly disabled?: boolean;
  readonly onConnect: (toolkit: string) => void;
}

function ToolkitLogo({
  toolkit,
  name,
  className,
}: {
  toolkit: string;
  name: string;
  className?: string;
}) {
  const { t } = useI18n();
  const card = appCardForToolkit(toolkit);
  const Icon = card.icon;
  const [logoFailed, setLogoFailed] = useState(false);

  if (logoFailed) {
    return (
      <span
        className={cn("flex size-4 shrink-0 items-center justify-center rounded-[4px]", className)}
        style={{ backgroundColor: card.brand }}
        aria-hidden="true"
      >
        <Icon className="size-3" style={{ color: card.glyph }} />
      </span>
    );
  }

  return (
    <img
      src={`https://logos.composio.dev/api/${toolkit}`}
      alt={t("connect.logoAlt", { name })}
      className={cn("size-4 shrink-0 object-contain", className)}
      loading="lazy"
      onError={() => setLogoFailed(true)}
    />
  );
}

export function ComposerUnconnectedToolsStrip({
  connectedToolkits,
  connectingToolkits = EMPTY_TOOLKIT_SET,
  disabled = false,
  onConnect,
}: ComposerUnconnectedToolsStripProps) {
  const { t } = useI18n();
  const unconnected = useMemo(
    () =>
      COMPOSER_FEATURED_TOOLKITS.filter((toolkit) => !connectedToolkits.has(toolkit)).map(
        (toolkit) => appCardForToolkit(toolkit),
      ),
    [connectedToolkits],
  );

  if (unconnected.length === 0) {
    return null;
  }

  return (
    <div
      className="composer-unconnected-tools"
      role="list"
      aria-label={t("composer.unconnectedTools")}
    >
      {unconnected.map((card) => {
        const connecting = connectingToolkits.has(card.toolkit);
        return (
          <button
            key={card.toolkit}
            type="button"
            role="listitem"
            disabled={disabled || connecting}
            aria-label={t("connect.connectApp", { name: card.name })}
            title={
              connecting ? t("connect.connecting") : t("connect.connectApp", { name: card.name })
            }
            className="composer-unconnected-tools-item group"
            onClick={() => onConnect(card.toolkit)}
          >
            <ToolkitLogo toolkit={card.toolkit} name={card.name} />
            <span className="composer-unconnected-tools-label" aria-hidden="true">
              {connecting ? t("connect.connecting") : t("connect.connect")}
            </span>
          </button>
        );
      })}
    </div>
  );
}
